import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useAuth,
} from "../../auth/AuthContext";

import {
  adicionarAlunoNaTurma,
  createAluno,
  createTurma,
  desativarTurma,
  editarTurma,
  loadAlunos,
  loadAlunosDaTurma,
  loadQuadrasAtivas,
  loadTurmas,
  reativarTurma,
  removerAlunoDaTurma,
} from "./turmas.service";

import type {
  Aluno,
  AlunoDaTurma,
  DiaSemana,
  NovaTurmaInput,
  NovoAlunoInput,
  QuadraOption,
  TipoTurma,
  Turma,
} from "./types";

import AlunoModal from "./components/AlunoModal";
import TurmaDetalhes from "./components/TurmaDetalhes";

import "./Turmas.css";


const DAYS:
  DiaSemana[] = [
    "Seg",
    "Ter",
    "Qua",
    "Qui",
    "Sex",
    "Sáb",
    "Dom",
  ];


const LEVELS = [
  "Iniciante",
  "D",
  "C",
  "B",
  "A",
];


const INITIAL_FORM:
  NovaTurmaInput = {
    nome: "",

    professor: "",

    dias: [],

    hora: "18:00",

    nivel:
      "Iniciante",

    capacidade: 8,

    tipo:
      "arena",

    quadra_id: "",

    duracao: 1,

    bloquear_quadra:
      true,
  };


function formatDias(
  dias: string[],
) {
  if (
    !dias ||
    dias.length === 0
  ) {
    return "Sem dias definidos";
  }

  return dias.join(", ");
}


function formatDuration(
  duration: number,
) {
  if (duration === 0.5) {
    return "30 min";
  }

  if (duration === 1) {
    return "1h";
  }

  if (duration === 1.5) {
    return "1h30";
  }

  if (
    Number.isInteger(
      duration,
    )
  ) {
    return `${duration}h`;
  }

  return `${Math.floor(
    duration,
  )}h30`;
}


export default function Turmas() {
  const {
    can,
  } =
    useAuth();


  const [
    turmas,
    setTurmas,
  ] =
    useState<Turma[]>([]);


  const [
    quadras,
    setQuadras,
  ] =
    useState<
      QuadraOption[]
    >([]);


  const [
    loading,
    setLoading,
  ] =
    useState(true);


  const [
    saving,
    setSaving,
  ] =
    useState(false);


  const [
    error,
    setError,
  ] =
    useState("");


  const [
    formError,
    setFormError,
  ] =
    useState("");


  const [
    search,
    setSearch,
  ] =
    useState("");

    const [
  statusFilter,
  setStatusFilter,
] =
  useState<
    "ativas" | "encerradas"
  >(
    "ativas",
  );


  const [
    showNew,
    setShowNew,
  ] =
    useState(false);

  const [
  editingTurma,
  setEditingTurma,
] =
  useState<Turma | null>(
    null,
  );

const [
  closingTurma,
  setClosingTurma,
] =
  useState<Turma | null>(
    null,
  );  


  const [
    form,
    setForm,
  ] =
    useState<NovaTurmaInput>(
      INITIAL_FORM,
    );


  /*
   * =======================================================
   * DETALHES DA TURMA / ALUNOS
   * =======================================================
   */

  const [
    selectedTurma,
    setSelectedTurma,
  ] =
    useState<Turma | null>(
      null,
    );


  const [
    alunosDaTurma,
    setAlunosDaTurma,
  ] =
    useState<
      AlunoDaTurma[]
    >([]);


  const [
    alunos,
    setAlunos,
  ] =
    useState<Aluno[]>([]);


  const [
    loadingAlunos,
    setLoadingAlunos,
  ] =
    useState(false);


  const [
    alunoModalOpen,
    setAlunoModalOpen,
  ] =
    useState(false);


  const [
    alunoSaving,
    setAlunoSaving,
  ] =
    useState(false);


  const [
    alunoError,
    setAlunoError,
  ] =
    useState("");


  useEffect(() => {
    async function load() {
      try {
        setLoading(
          true,
        );

        const [
          turmasResult,
          quadrasResult,
        ] =
          await Promise.all([
            loadTurmas(),
            loadQuadrasAtivas(),
          ]);


        setTurmas(
          turmasResult,
        );

        setQuadras(
          quadrasResult,
        );

        setError("");
      } catch (err) {
        console.error(
          err,
        );

        setError(
          "Não foi possível carregar as turmas.",
        );
      } finally {
        setLoading(
          false,
        );
      }
    }


    load();
  }, []);


  const filteredTurmas =
  useMemo(
    () => {
      const term =
        search
          .trim()
          .toLowerCase();

      return turmas.filter(
        (turma) => {
          const matchesStatus =
            statusFilter ===
            "ativas"
              ? turma.ativa
              : !turma.ativa;

          if (!matchesStatus) {
            return false;
          }

          if (!term) {
            return true;
          }

          return (
            turma.nome
              .toLowerCase()
              .includes(
                term,
              ) ||
            (
              turma.professor ??
              ""
            )
              .toLowerCase()
              .includes(
                term,
              ) ||
            turma.nivel
              .toLowerCase()
              .includes(
                term,
              )
          );
        },
      );
    },
    [
      turmas,
      search,
      statusFilter,
    ],
  );


  const turmasArena =
    turmas.filter(
      (turma) =>
        turma.tipo ===
          "arena" &&
        turma.ativa,
    ).length;


  const turmasExternas =
    turmas.filter(
      (turma) =>
        turma.tipo ===
          "externa" &&
        turma.ativa,
    ).length;


  const alunosDisponiveis =
    useMemo(
      () => {
        const vinculados =
          new Set(
            alunosDaTurma.map(
              (item) =>
                item.aluno.id,
            ),
          );


        return alunos.filter(
          (aluno) =>
            !vinculados.has(
              aluno.id,
            ),
        );
      },
      [
        alunos,
        alunosDaTurma,
      ],
    );


  async function openTurma(
    turma: Turma,
  ) {
    if (
      turma.tipo !==
      "arena"
    ) {
      return;
    }


    setSelectedTurma(
      turma,
    );

    setAlunoError("");

    setLoadingAlunos(
      true,
    );


    try {
      const result =
        await loadAlunosDaTurma(
          turma.id,
        );


      setAlunosDaTurma(
        result,
      );
    } catch (err) {
      console.error(
        err,
      );

      setAlunosDaTurma(
        [],
      );

      setAlunoError(
        "Não foi possível carregar os alunos da turma.",
      );
    } finally {
      setLoadingAlunos(
        false,
      );
    }
  }


  function closeTurma() {
    if (
      alunoSaving
    ) {
      return;
    }


    setSelectedTurma(
      null,
    );

    setAlunosDaTurma(
      [],
    );

    setAlunoModalOpen(
      false,
    );

    setAlunoError("");
  }

  async function handleCloseTurma() {
  if (!closingTurma) {
    return;
  }

  try {
    setSaving(
      true,
    );

    setFormError("");

    const deactivated =
  await desativarTurma(
    closingTurma.id,
    closingTurma.updated_at ??
      null,
  );

const quadra =
  quadras.find(
    (item) =>
      item.id ===
      deactivated.quadra_id,
  );

const complete:
  Turma = {
    ...deactivated,

    quadra_nome:
      quadra?.nome ??
      null,
  };

setTurmas(
  (current) =>
    current.map(
      (turma) =>
        turma.id ===
        complete.id
          ? complete
          : turma,
    ),
);

    if (
      selectedTurma?.id ===
      closingTurma.id
    ) {
      setSelectedTurma(
        null,
      );
    }

    setClosingTurma(
      null,
    );
    setStatusFilter(
  "encerradas",
);
  } catch (err) {
    console.error(
      "Erro ao encerrar turma:",
      err,
    );

    let message =
      "Não foi possível encerrar a turma.";

    if (
      typeof err === "object" &&
      err !== null &&
      "message" in err
    ) {
      const errorMessage =
        String(
          (
            err as {
              message?: string;
            }
          ).message ??
          "",
        ).trim();

      if (errorMessage) {
        message =
          errorMessage;
      }
    }

    setFormError(
      message,
    );
  } finally {
    setSaving(
      false,
    );
  }
}


  async function openAlunoModal() {
    if (
      !selectedTurma ||
      !can(
        "turmas.edit",
      )
    ) {
      return;
    }


    if (
      alunosDaTurma.length >=
      selectedTurma.capacidade
    ) {
      return;
    }


    try {
      setLoadingAlunos(
        true,
      );

      setAlunoError("");


      const result =
        await loadAlunos();


      setAlunos(
        result,
      );

      setAlunoModalOpen(
        true,
      );
    } catch (err) {
      console.error(
        err,
      );

      setAlunoError(
        "Não foi possível carregar os alunos cadastrados.",
      );

      setAlunoModalOpen(
        true,
      );
    } finally {
      setLoadingAlunos(
        false,
      );
    }
  }


  function closeAlunoModal() {
    if (
      alunoSaving
    ) {
      return;
    }


    setAlunoModalOpen(
      false,
    );

    setAlunoError("");
  }


  async function handleAdicionarAlunoExistente(
    aluno: Aluno,
  ) {
    if (
      !selectedTurma
    ) {
      return;
    }


    if (
      alunosDaTurma.length >=
      selectedTurma.capacidade
    ) {
      setAlunoError(
        "A turma já atingiu a capacidade máxima.",
      );

      return;
    }


    try {
      setAlunoSaving(
        true,
      );

      setAlunoError("");


      await adicionarAlunoNaTurma(
        selectedTurma.id,
        aluno.id,
      );


      const updated =
        await loadAlunosDaTurma(
          selectedTurma.id,
        );


      setAlunosDaTurma(
        updated,
      );

      setAlunoModalOpen(
        false,
      );
    } catch (err) {
  console.error(
    "Erro ao adicionar aluno à turma:",
    err,
  );

  let message =
    "Não foi possível adicionar o aluno à turma.";

  if (
    typeof err === "object" &&
    err !== null &&
    "message" in err
  ) {
    const errorMessage =
      String(
        (
          err as {
            message?: string;
          }
        ).message ?? "",
      ).trim();

    if (errorMessage) {
      message =
        errorMessage;
    }
  }

  setAlunoError(
    message,
  );
} finally {
      setAlunoSaving(
        false,
      );
    }
  }


  async function handleCadastrarNovoAluno(
    input: NovoAlunoInput,
  ) {
    if (
      !selectedTurma
    ) {
      return;
    }


    if (
      alunosDaTurma.length >=
      selectedTurma.capacidade
    ) {
      setAlunoError(
        "A turma já atingiu a capacidade máxima.",
      );

      return;
    }


    try {
      setAlunoSaving(
        true,
      );

      setAlunoError("");


      const created =
        await createAluno(
          input,
        );


      await adicionarAlunoNaTurma(
        selectedTurma.id,
        created.id,
      );


      setAlunos(
        (current) =>
          [
            ...current,
            created,
          ].sort(
            (a, b) =>
              a.nome.localeCompare(
                b.nome,
                "pt-BR",
              ),
          ),
      );


      const updated =
        await loadAlunosDaTurma(
          selectedTurma.id,
        );


      setAlunosDaTurma(
        updated,
      );

      setAlunoModalOpen(
        false,
      );
    } catch (err) {
      console.error(
        err,
      );

      setAlunoError(
        err instanceof Error
          ? err.message
          : "Não foi possível cadastrar o aluno.",
      );
    } finally {
      setAlunoSaving(
        false,
      );
    }
  }


  async function handleRemoverAluno(
    item: AlunoDaTurma,
  ) {
    if (
      !selectedTurma ||
      !can(
        "turmas.edit",
      )
    ) {
      return;
    }


    const confirmed =
      window.confirm(
        `Remover ${item.aluno.nome} desta turma?`,
      );


    if (!confirmed) {
      return;
    }


    try {
      setLoadingAlunos(
        true,
      );

      setAlunoError("");


      await removerAlunoDaTurma(
        item.vinculo_id,
      );


      setAlunosDaTurma(
        (current) =>
          current.filter(
            (aluno) =>
              aluno.vinculo_id !==
              item.vinculo_id,
          ),
      );
    } catch (err) {
      console.error(
        err,
      );

      setAlunoError(
        err instanceof Error
          ? err.message
          : "Não foi possível remover o aluno da turma.",
      );
    } finally {
      setLoadingAlunos(
        false,
      );
    }
  }


  function openNewTurma() {
  setEditingTurma(
    null,
  );

  setForm({
    ...INITIAL_FORM,

    quadra_id:
      quadras[0]?.id ??
      "",
  });

  setFormError("");

  setShowNew(
    true,
  );
}

  function openEditTurma(
  turma: Turma,
) {
  setEditingTurma(
    turma,
  );

  setForm({
    nome:
      turma.nome,

    professor:
      turma.professor ??
      "",

    dias:
      turma.dias,

    hora:
      turma.hora,

    nivel:
      turma.nivel,

    capacidade:
      turma.capacidade,

    tipo:
      turma.tipo,

    quadra_id:
      turma.quadra_id ??
      "",

    duracao:
      turma.duracao,

    bloquear_quadra:
      turma.bloquear_quadra,
  });

  setFormError("");

  setShowNew(
    true,
  );
}


function requestCloseTurma(
  turma: Turma,
) {
  setClosingTurma(
    turma,
  );
}


  function closeNewTurma() {
  if (saving) {
    return;
  }

  setShowNew(
    false,
  );

  setEditingTurma(
    null,
  );

  setFormError("");

  setForm(
    INITIAL_FORM,
  );
}


  function toggleDay(
    day: DiaSemana,
  ) {
    setForm(
      (current) => ({
        ...current,

        dias:
          current.dias.includes(
            day,
          )
            ? current.dias.filter(
                (item) =>
                  item !== day,
              )
            : [
                ...current.dias,
                day,
              ],
      }),
    );
  }


  async function handleSaveTurma() {
  if (
    !form.nome.trim()
  ) {
    setFormError(
      "Informe o nome da turma.",
    );

    return;
  }

  if (
    !form.professor.trim()
  ) {
    setFormError(
      "Informe o professor.",
    );

    return;
  }

  if (
    form.dias.length ===
    0
  ) {
    setFormError(
      "Selecione pelo menos um dia da semana.",
    );

    return;
  }

  if (
    !form.quadra_id
  ) {
    setFormError(
      "Selecione a quadra.",
    );

    return;
  }

  try {
    setSaving(
      true,
    );

    setFormError("");

    let saved:
      Turma;

    if (
      editingTurma
    ) {
      saved =
        await editarTurma({
          turmaId:
            editingTurma.id,

          nome:
            form.nome,

          professor:
            form.professor,

          dias:
            form.dias,

          hora:
            form.hora,

          nivel:
            form.nivel,

          capacidade:
            form.capacidade,

          tipo:
            form.tipo,

          quadraId:
            form.quadra_id,

          duracao:
            form.duracao,

          bloquearQuadra:
            form.bloquear_quadra,

          updatedAtEsperado:
            editingTurma.updated_at ??
            null,
        });
    } else {
      saved =
        await createTurma(
          form,
        );
    }

    const quadra =
      quadras.find(
        (item) =>
          item.id ===
          saved.quadra_id,
      );

    const complete:
      Turma = {
        ...saved,

        quadra_nome:
          quadra?.nome ??
          null,
      };

    if (
      editingTurma
    ) {
      setTurmas(
        (current) =>
          current.map(
            (turma) =>
              turma.id ===
              complete.id
                ? complete
                : turma,
          ),
      );

      if (
        selectedTurma?.id ===
        complete.id
      ) {
        setSelectedTurma(
          complete,
        );
      }
    } else {
      setTurmas(
        (current) => [
          ...current,
          complete,
        ],
      );
    }

    closeNewTurma();
  } catch (err) {
    console.error(
      "Erro ao salvar turma:",
      err,
    );

    let message =
      editingTurma
        ? "Não foi possível editar a turma."
        : "Não foi possível cadastrar a turma.";

    if (
      typeof err === "object" &&
      err !== null &&
      "message" in err
    ) {
      const errorMessage =
        String(
          (
            err as {
              message?: string;
            }
          ).message ??
          "",
        ).trim();

      if (errorMessage) {
        message =
          errorMessage;
      }
    }

    setFormError(
      message,
    );
  } finally {
    setSaving(
      false,
    );
  }
}


  if (loading) {
    return (
      <section className="turmas-page">
        <div className="turmas-empty">
          Carregando aulas...
        </div>
      </section>
    );
  }


  if (error) {
    return (
      <section className="turmas-page">
        <div className="turmas-empty turmas-error">
          {error}
        </div>
      </section>
    );
  }


  if (
    selectedTurma
  ) {
    return (
      <>
        <TurmaDetalhes
          turma={
            selectedTurma
          }
          alunos={
            alunosDaTurma
          }
          loading={
            loadingAlunos
          }
          canManage={
            can(
              "turmas.edit",
            )
          }
          onVoltar={
            closeTurma
          }
          onAdicionarAluno={
            openAlunoModal
          }
          onRemoverAluno={
            handleRemoverAluno
          }
        />


        <AlunoModal
          open={
            alunoModalOpen
          }
          alunos={
            alunosDisponiveis
          }
          loading={
            loadingAlunos
          }
          saving={
            alunoSaving
          }
          error={
            alunoError
          }
          onClose={
            closeAlunoModal
          }
          onAdicionarExistente={
            handleAdicionarAlunoExistente
          }
          onCadastrarNovo={
            handleCadastrarNovoAluno
          }
        />
      </>
    );
  }


  return (
    <section className="turmas-page">
      <div className="turmas-header">
        <div>
          <span className="turmas-eyebrow">
            AULAS
          </span>

          <h2>
            Turmas e aulas
          </h2>

          <p>
            Organize turmas,
            professores e horários
            fixos das quadras.
          </p>
        </div>


        {can(
          "turmas.create",
        ) && (
          <button
            type="button"
            className="turmas-primary-button"
            onClick={
              openNewTurma
            }
          >
            + Nova turma
          </button>
        )}
      </div>


      <div className="turmas-stats">
        <div className="turmas-stat-card">
          <span>
            Turmas da Arena
          </span>

          <strong>
            {turmasArena}
          </strong>
        </div>

        <div className="turmas-stat-card">
          <span>
            Professores externos
          </span>

          <strong>
            {turmasExternas}
          </strong>
        </div>

        <div className="turmas-stat-card">
          <span>
            Quadras disponíveis
          </span>

          <strong>
            {quadras.length}
          </strong>
        </div>

        <div className="turmas-stat-card">
          <span>
            Total cadastrado
          </span>

          <strong>
            {turmas.length}
          </strong>
        </div>
      </div>


      <div className="turmas-toolbar">

  <div className="turmas-status-tabs">
    <button
      type="button"
      className={
        statusFilter === "ativas"
          ? "active"
          : ""
      }
      onClick={() =>
        setStatusFilter("ativas")
      }
    >
      Ativas
    </button>

    <button
      type="button"
      className={
        statusFilter === "encerradas"
          ? "active"
          : ""
      }
      onClick={() =>
        setStatusFilter("encerradas")
      }
    >
      Encerradas
    </button>
  </div>

  <input
    value={
      search
    }
    onChange={(
      event,
    ) =>
      setSearch(
        event.target
          .value,
      )
    }
    placeholder="Buscar turma, professor ou nível..."
  />

</div>


      {filteredTurmas.length ===
      0 ? (
        <div className="turmas-empty">
          <strong>
            Nenhuma turma cadastrada.
          </strong>

          <span>
            Cadastre uma turma da Arena
            ou um horário recorrente de
            professor externo.
          </span>
        </div>
      ) : (
        <div className="turmas-grid">
          {filteredTurmas.map(
            (turma) => (
              <article
                key={
                  turma.id
                }
                className="turma-card"
              >
                <div className="turma-card-top">
                  <div>
                    <span
                      className={
                        turma.tipo ===
                        "arena"
                          ? "turma-type turma-type-arena"
                          : "turma-type turma-type-externa"
                      }
                    >
                      {turma.tipo ===
                      "arena"
                        ? "Arena"
                        : "Professor externo"}
                    </span>

                    <h3>
                      {
                        turma.nome
                      }
                    </h3>
                  </div>

                  <span
  className={
    turma.ativa
      ? "turma-status turma-status-active"
      : "turma-status turma-status-inactive"
  }
>
  {turma.ativa
    ? "Ativa"
    : "Encerrada"}
</span>
                </div>


                <div className="turma-info-grid">
                  <div>
                    <span>
                      Professor
                    </span>

                    <strong>
                      {turma.professor ||
                        "Não informado"}
                    </strong>
                  </div>

                  <div>
                    <span>
                      Nível
                    </span>

                    <strong>
                      {
                        turma.nivel
                      }
                    </strong>
                  </div>

                  <div>
                    <span>
                      Dias
                    </span>

                    <strong>
                      {formatDias(
                        turma.dias,
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>
                      Horário
                    </span>

                    <strong>
                      {
                        turma.hora
                      }
                    </strong>
                  </div>

                  <div>
                    <span>
                      Duração
                    </span>

                    <strong>
                      {formatDuration(
                        turma.duracao,
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>
                      Quadra
                    </span>

                    <strong>
                      {turma.quadra_nome ||
                        "Não definida"}
                    </strong>
                  </div>
                </div>


                {turma.bloquear_quadra && (
                  <div className="turma-recurring-badge">
                    ✓ Bloqueio recorrente da quadra
                  </div>
                )}


                {turma.tipo ===
                  "arena" && (
                  <div className="turma-capacity">
                    Capacidade:{" "}
                    <strong>
                      {
                        turma.capacidade
                      }{" "}
                      alunos
                    </strong>
                  </div>
                )}


                <button
                  type="button"
                  className="turma-open-button"
                  onClick={() =>
                    openTurma(
                      turma,
                    )
                  }
                  disabled={
                    turma.tipo !==
                    "arena"
                  }
                >
                          {turma.tipo ===
        "arena"
          ? "Abrir turma"
          : "Horário externo"}
      </button>

        {can("turmas.edit") && (
  <div className="turma-card-actions">
    {turma.ativa ? (
      <>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();

            openEditTurma(
              turma,
            );
          }}
        >
          Editar turma
        </button>

        <button
          type="button"
          className="turma-close-button"
          onClick={(event) => {
            event.stopPropagation();

            requestCloseTurma(
              turma,
            );
          }}
        >
          Encerrar turma
        </button>
      </>
    ) : (
      <button
        type="button"
        className="turma-reactivate-button"
        onClick={async (
          event,
        ) => {
          event.stopPropagation();

          try {
            setSaving(
              true,
            );

            setFormError(
              "",
            );

            const reactivated =
              await reativarTurma(
                turma.id,
                turma.updated_at ??
                  null,
              );

            const quadra =
              quadras.find(
                (item) =>
                  item.id ===
                  reactivated.quadra_id,
              );

            const complete:
              Turma = {
                ...reactivated,

                quadra_nome:
                  quadra?.nome ??
                  null,
              };

            setTurmas(
              (current) =>
                current.map(
                  (item) =>
                    item.id ===
                    complete.id
                      ? complete
                      : item,
                ),
            );

            setStatusFilter(
              "ativas",
            );
          } catch (err) {
            console.error(
              "Erro ao reativar turma:",
              err,
            );

            let message =
              "Não foi possível reativar a turma.";

            if (
              typeof err === "object" &&
              err !== null &&
              "message" in err
            ) {
              const errorMessage =
                String(
                  (
                    err as {
                      message?: string;
                    }
                  ).message ??
                    "",
                ).trim();

              if (errorMessage) {
                message =
                  errorMessage;
              }
            }

            setFormError(
              message,
            );
          } finally {
            setSaving(
              false,
            );
          }
        }}
        disabled={
          saving
        }
      >
        {saving
          ? "Reativando..."
          : "Reativar turma"}
      </button>
    )}
  </div>
)}
    </article>
            ),
          )}
        </div>
      )}


      {showNew && (
        <div
          className="turma-modal-backdrop"
          onClick={
            closeNewTurma
          }
        >
          <div
            className="turma-modal"
            onClick={(
              event,
            ) =>
              event.stopPropagation()
            }
          >
            <div className="turma-modal-header">
              <div>
                <span className="turmas-eyebrow">
  {editingTurma
    ? "EDITAR TURMA"
    : "NOVA TURMA"}
</span>

<h3>
  {editingTurma
    ? "Alterar dados da turma"
    : "Cadastrar horário"}
</h3>
              </div>

              <button
                type="button"
                onClick={
                  closeNewTurma
                }
                disabled={
                  saving
                }
              >
                ×
              </button>
            </div>


            <label className="turma-field">
              Tipo

              <select
                value={
                  form.tipo
                }
                onChange={(
                  event,
                ) =>
                  setForm(
                    (current) => ({
                      ...current,

                      tipo:
                        event.target
                          .value as TipoTurma,
                    }),
                  )
                }
              >
                <option value="arena">
                  Turma da Arena
                </option>

                <option value="externa">
                  Professor externo
                </option>
              </select>
            </label>


            <label className="turma-field">
              Nome da turma

              <input
                value={
                  form.nome
                }
                onChange={(
                  event,
                ) =>
                  setForm(
                    (current) => ({
                      ...current,

                      nome:
                        event.target
                          .value,
                    }),
                  )
                }
                placeholder={
                  form.tipo ===
                  "arena"
                    ? "Ex.: Iniciante - Noite"
                    : "Ex.: Aula Prof. Carlos"
                }
              />
            </label>


            <label className="turma-field">
              Professor

              <input
                value={
                  form.professor
                }
                onChange={(
                  event,
                ) =>
                  setForm(
                    (current) => ({
                      ...current,

                      professor:
                        event.target
                          .value,
                    }),
                  )
                }
                placeholder="Nome do professor"
              />
            </label>


            {form.tipo ===
              "arena" && (
              <label className="turma-field">
                Nível

                <select
                  value={
                    form.nivel
                  }
                  onChange={(
                    event,
                  ) =>
                    setForm(
                      (current) => ({
                        ...current,

                        nivel:
                          event.target
                            .value,
                      }),
                    )
                  }
                >
                  {LEVELS.map(
                    (level) => (
                      <option
                        key={
                          level
                        }
                        value={
                          level
                        }
                      >
                        {level}
                      </option>
                    ),
                  )}
                </select>
              </label>
            )}


            <div className="turma-field">
              <span>
                Dias da semana
              </span>

              <div className="turma-days">
                {DAYS.map(
                  (day) => (
                    <button
                      key={
                        day
                      }
                      type="button"
                      className={
                        form.dias.includes(
                          day,
                        )
                          ? "turma-day active"
                          : "turma-day"
                      }
                      onClick={() =>
                        toggleDay(
                          day,
                        )
                      }
                    >
                      {day}
                    </button>
                  ),
                )}
              </div>
            </div>


            <div className="turma-row">
              <label className="turma-field">
                Horário

                <input
                  type="time"
                  step="1800"
                  value={
                    form.hora
                  }
                  onChange={(
                    event,
                  ) =>
                    setForm(
                      (current) => ({
                        ...current,

                        hora:
                          event.target
                            .value,
                      }),
                    )
                  }
                />
              </label>


              <label className="turma-field">
                Duração

                <select
                  value={
                    form.duracao
                  }
                  onChange={(
                    event,
                  ) =>
                    setForm(
                      (current) => ({
                        ...current,

                        duracao:
                          Number(
                            event.target
                              .value,
                          ),
                      }),
                    )
                  }
                >
                  <option value={0.5}>
                    30 min
                  </option>

                  <option value={1}>
                    1h
                  </option>

                  <option value={1.5}>
                    1h30
                  </option>

                  <option value={2}>
                    2h
                  </option>
                </select>
              </label>
            </div>


            <label className="turma-field">
              Quadra

              <select
                value={
                  form.quadra_id
                }
                onChange={(
                  event,
                ) =>
                  setForm(
                    (current) => ({
                      ...current,

                      quadra_id:
                        event.target
                          .value,
                    }),
                  )
                }
              >
                {quadras.map(
                  (quadra) => (
                    <option
                      key={
                        quadra.id
                      }
                      value={
                        quadra.id
                      }
                    >
                      {
                        quadra.nome
                      }
                    </option>
                  ),
                )}
              </select>
            </label>


            {form.tipo ===
              "arena" && (
              <label className="turma-field">
                Capacidade

                <input
                  type="number"
                  min={1}
                  max={50}
                  value={
                    form.capacidade
                  }
                  onChange={(
                    event,
                  ) =>
                    setForm(
                      (current) => ({
                        ...current,

                        capacidade:
                          Math.max(
                            1,
                            Number(
                              event.target
                                .value,
                            ),
                          ),
                      }),
                    )
                  }
                />
              </label>
            )}


            <label className="turma-checkbox">
              <input
                type="checkbox"
                checked={
                  form.bloquear_quadra
                }
                onChange={(
                  event,
                ) =>
                  setForm(
                    (current) => ({
                      ...current,

                      bloquear_quadra:
                        event.target
                          .checked,
                    }),
                  )
                }
              />

              <div>
                <strong>
                  Bloquear automaticamente a quadra
                </strong>

                <span>
                  Este horário será considerado
                  ocupado semanalmente na Agenda.
                </span>
              </div>
            </label>


            {formError && (
              <div className="turmas-form-error">
                {formError}
              </div>
            )}


            <div className="turma-modal-actions">
              <button
                type="button"
                className="turma-cancel-button"
                onClick={
                  closeNewTurma
                }
                disabled={
                  saving
                }
              >
                Cancelar
              </button>

              <button
                type="button"
                className="turma-save-button"
                onClick={
  handleSaveTurma
}
                disabled={
                  saving
                }
              >
                {saving
  ? "Salvando..."
  : editingTurma
    ? "Salvar alterações"
    : "Cadastrar turma"}
              </button>
            </div>
          </div>
        </div>
      )}
      {closingTurma && (
  <div
    className="turma-modal-backdrop"
    onClick={() => {
      if (!saving) {
        setClosingTurma(
          null,
        );

        setFormError(
          "",
        );
      }
    }}
  >
    <div
      className="turma-modal"
      onClick={(event) =>
        event.stopPropagation()
      }
    >
      <div className="turma-modal-header">
        <div>
          <span className="turmas-eyebrow">
            ENCERRAR TURMA
          </span>

          <h3>
            {closingTurma.nome}
          </h3>
        </div>

        <button
          type="button"
          onClick={() => {
            if (!saving) {
              setClosingTurma(
                null,
              );

              setFormError(
                "",
              );
            }
          }}
          disabled={
            saving
          }
        >
          ×
        </button>
      </div>

      <div className="turmas-close-warning">
        <strong>
          Deseja realmente encerrar esta turma?
        </strong>

        <p>
          Ela deixará de aparecer entre as
          turmas ativas e não bloqueará mais
          a quadra.
        </p>

        <p>
          Histórico, alunos, chamadas e
          pacotes serão preservados.
        </p>
      </div>

      {formError && (
        <div className="turmas-form-error">
          {formError}
        </div>
      )}

      <div className="turma-modal-actions">
        <button
          type="button"
          className="turma-cancel-button"
          onClick={() => {
            if (!saving) {
              setClosingTurma(
                null,
              );

              setFormError(
                "",
              );
            }
          }}
          disabled={
            saving
          }
        >
          Cancelar
        </button>

        <button
          type="button"
          className="turma-close-confirm"
          onClick={
            handleCloseTurma
          }
          disabled={
            saving
          }
        >
          {saving
            ? "Encerrando..."
            : "Encerrar turma"}
        </button>
      </div>
    </div>
  </div>
)}
    </section>
  );
}